<template>
  <div class="flex flex-col gap-3">
    <!-- Identity card -->
    <div class="bg-white border border-gray-200 rounded-xl p-4">
      <div class="flex items-center gap-3">
        <Avatar
          v-if="member.profileImage"
          :image="member.profileImage"
          shape="circle"
          class="!w-14 !h-14 flex-none"
        />
        <div
          v-else
          class="w-14 h-14 rounded-full flex items-center justify-center font-semibold text-lg flex-none"
          :style="avatarStyle(member)"
        >{{ initials(member.fullName) }}</div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-1.5 flex-wrap">
            <span class="text-base font-semibold truncate">{{ member.fullName }}</span>
            <span
              v-if="isManager"
              class="text-[10px] font-semibold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full"
            >מנהל</span>
          </div>
          <div class="text-xs text-gray-500 mt-0.5 truncate">{{ roleLabel }}{{ primaryBranch ? ` · ${primaryBranch.name}` : '' }}</div>
        </div>
      </div>

      <div v-if="member.phoneNumber && !isManager" class="flex gap-2 mt-3.5">
        <a
          v-if="member.phoneNumber"
          :href="`tel:${member.phoneNumber}`"
          class="flex-1 flex items-center justify-center gap-1.5 py-2.5 border border-gray-200 rounded-lg text-[12.5px] font-medium"
        >
          <i class="pi pi-phone text-primary text-sm" /> חיוג
        </a>
        <button
          v-if="member.phoneNumber"
          type="button"
          class="flex-1 flex items-center justify-center gap-1.5 py-2.5 border border-gray-200 rounded-lg text-[12.5px] font-medium"
          @click="whatsapp"
        >
          <i class="pi pi-whatsapp text-green-600 text-sm" /> וואטסאפ
        </button>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-3 gap-2">
      <div class="bg-white border border-gray-200 rounded-xl py-3 text-center">
        <div class="text-xl font-semibold text-gray-800">{{ services.length }}</div>
        <div class="text-[10.5px] text-gray-500 mt-0.5">שירותים</div>
      </div>
      <div class="bg-white border border-gray-200 rounded-xl py-3 text-center">
        <div class="text-xl font-semibold text-gray-800">{{ totalHours }}</div>
        <div class="text-[10.5px] text-gray-500 mt-0.5">שעות/שבוע</div>
      </div>
      <div class="bg-white border border-gray-200 rounded-xl py-3 text-center">
        <div class="text-xl font-semibold text-gray-800">{{ branches.length }}</div>
        <div class="text-[10.5px] text-gray-500 mt-0.5">סניפים</div>
      </div>
    </div>

    <!-- Contact -->
    <div class="text-[11px] font-semibold text-gray-500 mt-2 px-0.5">פרטי קשר</div>
    <div class="bg-white border border-gray-200 rounded-xl">
      <div class="flex items-center gap-2.5 px-3 py-2.5">
        <span class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-none"><i class="pi pi-phone text-sm" /></span>
        <span class="flex-1 text-[12.5px] text-gray-500">טלפון</span>
        <span class="text-[13px]" style="direction: ltr">{{ member.phoneNumber || '—' }}</span>
      </div>
      <div class="h-px bg-gray-100" />
      <div class="flex items-center gap-2.5 px-3 py-2.5">
        <span class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-none"><i class="pi pi-envelope text-sm" /></span>
        <span class="flex-1 text-[12.5px] text-gray-500">אימייל</span>
        <span class="text-[13px] truncate max-w-[55%]" style="direction: ltr">{{ member.email || '—' }}</span>
      </div>
    </div>

    <!-- Branches -->
    <div class="text-[11px] font-semibold text-gray-500 mt-2 px-0.5">סניפים</div>
    <div v-if="branches.length" class="flex flex-col gap-2">
      <div v-for="b in branches" :key="b.id" class="bg-white border border-gray-200 rounded-xl flex items-center gap-2.5 px-3 py-2.5">
        <span class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-none"><i class="pi pi-map-marker text-sm" /></span>
        <div class="flex-1 min-w-0">
          <div class="text-[13px] font-medium truncate">{{ b.name }}</div>
          <div v-if="b.address" class="text-[11px] text-gray-500 truncate">{{ b.address }}</div>
        </div>
        <span class="text-[11.5px] text-gray-500 font-medium flex-none">{{ b.hours }} שעות</span>
      </div>
    </div>
    <div v-else class="text-xs text-gray-400">לא שויך לסניף — קבעו שעות בלשונית "שעות וסניפים"</div>

    <!-- Services -->
    <div class="text-[11px] font-semibold text-gray-500 mt-2 px-0.5">שירותים · {{ services.length }}</div>
    <div v-if="services.length" class="flex flex-wrap gap-1.5">
      <span
        v-for="s in services"
        :key="s.id"
        class="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full"
      >{{ s.name }}</span>
    </div>
    <div v-else class="text-xs text-gray-400">לא נבחרו שירותים — בחרו בלשונית "שירותים"</div>

    <!-- Permissions -->
    <div class="text-[11px] font-semibold text-gray-500 mt-2 px-0.5">הרשאות</div>
    <div class="bg-white border border-gray-200 rounded-xl flex items-center gap-2.5 px-3 py-2.5 mb-1">
      <span class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-none"><i class="pi pi-briefcase text-sm" /></span>
      <div class="flex-1">
        <div class="text-[13px] font-medium">{{ roleLabel }}</div>
        <div class="text-[11px] text-gray-500">{{ isManager ? 'גישה מלאה לניהול העסק' : 'גישה לתורים וללקוחות שלו/ה' }}</div>
      </div>
    </div>

    <!-- Managers always see contact details, so the toggle only makes sense for the rest -->
    <div
      v-if="!isManager && canManage"
      class="bg-white border border-gray-200 rounded-xl flex items-center gap-2.5 px-3 py-2.5 mb-1"
    >
      <span class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-none"><i class="pi pi-id-card text-sm" /></span>
      <div class="flex-1">
        <div class="text-[13px] font-medium">צפייה בפרטי קשר של לקוחות</div>
        <div class="text-[11px] text-gray-500">
          {{ contactAccess ? 'רואה טלפון ואימייל של לקוחות' : 'לא רואה טלפון ואימייל — יכול/ה עדיין לשלוח הודעה' }}
        </div>
      </div>
      <ToggleSwitch
        :modelValue="contactAccess"
        :disabled="savingContactAccess"
        @update:modelValue="updateContactAccess" />
    </div>

    <!-- Branch calendar: an employee normally only sees their own day. Managers already
         see everything, so the toggle is meaningless for them. -->
    <div
      v-if="!isManager && canManage"
      class="bg-white border border-gray-200 rounded-xl flex items-center gap-2.5 px-3 py-2.5 mb-1"
    >
      <span class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-none"><i class="pi pi-calendar text-sm" /></span>
      <div class="flex-1">
        <div class="text-[13px] font-medium">צפייה ביומן הסניף</div>
        <div class="text-[11px] text-gray-500">
          {{ branchCalendarAccess
            ? `רואה את היומנים של כל הצוות ב${branchScopeLabel}`
            : 'רואה רק את היומן האישי שלו/ה' }}
        </div>
      </div>
      <ToggleSwitch
        :modelValue="branchCalendarAccess"
        :disabled="savingBranchCalendarAccess"
        @update:modelValue="updateBranchCalendarAccess" />
    </div>

    <!-- What this employee's calendar opens on. A starting point only — once they switch
         view or staff filter themselves, their own choice is what sticks. -->
    <template v-if="!isManager && canManage">
      <div class="text-[11px] font-semibold text-gray-500 mt-2 px-0.5">ברירת מחדל ליומן</div>
      <div class="bg-white border border-gray-200 rounded-xl px-3 py-3 mb-1 flex flex-col gap-3">
        <div>
          <div class="text-[12.5px] font-medium mb-1.5">תצוגה פותחת</div>
          <div class="flex bg-gray-100 rounded-lg p-0.5">
            <button
              v-for="opt in VIEW_OPTIONS"
              :key="opt.value"
              type="button"
              class="flex-1 py-1.5 rounded-md text-xs transition-colors"
              :class="defaultView === opt.value ? 'bg-white text-primary font-medium shadow-sm' : 'text-gray-500'"
              :disabled="savingCalendarDefaults"
              @click="updateCalendarDefaults({ view: opt.value })"
            >{{ opt.label }}</button>
          </div>
        </div>

        <div v-if="branchCalendarAccess">
          <div class="text-[12.5px] font-medium mb-1.5">יומן פותח</div>
          <div class="flex bg-gray-100 rounded-lg p-0.5">
            <button
              v-for="opt in SCOPE_OPTIONS"
              :key="opt.value"
              type="button"
              class="flex-1 py-1.5 rounded-md text-xs transition-colors"
              :class="defaultScope === opt.value ? 'bg-white text-primary font-medium shadow-sm' : 'text-gray-500'"
              :disabled="savingCalendarDefaults"
              @click="updateCalendarDefaults({ scope: opt.value })"
            >{{ opt.label }}</button>
          </div>
        </div>
        <div v-else class="text-[11px] text-gray-400">
          היקף היומן ניתן לבחירה רק כשצפייה ביומן הסניף מופעלת
        </div>
      </div>
    </template>

    <button
      v-if="!member.main && canManage"
      type="button"
      class="text-center text-xs text-red-600 py-2"
      @click="confirmDeactivate"
    >השבתת עובד/ה</button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { CalendarDefaultScope, CalendarDefaultView, ClientRoleEnum, ICalendarDefaults, MAIN_BRANCH_ID } from '@torim-app/types';
import { sendWhatsApp } from '@torim-app/utils';
import Avatar from 'primevue/avatar';
import ToggleSwitch from 'primevue/toggleswitch';
import { useAdminSettingsStore, useAdminStore } from '@app/stores';
import { avatarStyle, initials, servicesForProvider, teamMemberBranchIds, weeklyHours } from './team-utils';
import { SettingsApi } from '@app/api/admin';
import { AdminRouteNames } from '@app/router/route-names';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useConfirm } from 'primevue/useconfirm';

const router = useRouter();
const toast = useToast();
const confirm = useConfirm();

const props = withDefaults(defineProps<{
  member: { userId: string; fullName: string; email: string; phoneNumber: string; role?: ClientRoleEnum; main: boolean; profileImage?: string; canViewCustomerContact?: boolean; canViewBranchCalendar?: boolean; calendarDefaults?: ICalendarDefaults };
  // Employees viewing their own profile don't get the manager-only actions.
  canManage?: boolean;
}>(), { canManage: true });

const adminStore = useAdminStore();
const settingsStore = useAdminSettingsStore();

const services = computed(() => servicesForProvider(props.member.userId));
const totalHours = computed(() => weeklyHours(props.member.userId));

const branches = computed(() => {
  const ids = teamMemberBranchIds(props.member.userId);
  return adminStore.getBranchSelectOptions
    .filter(b => ids.includes(b.id))
    .map(b => ({ id: b.id, name: b.name, address: b.address, hours: weeklyHours(props.member.userId, b.id) }));
});
const primaryBranch = computed(() => branches.value[0] ?? adminStore.getBranchSelectOptions.find(b => b.id === MAIN_BRANCH_ID));

const isManager = computed(() => props.member.main || props.member.role === ClientRoleEnum.Admin || props.member.role === ClientRoleEnum.Operator);
const roleLabel = computed(() => {
  if (props.member.main) return 'מנהל ראשי';
  switch (props.member.role) {
    case ClientRoleEnum.Operator: return 'מנהל/ת';
    case ClientRoleEnum.ReadOnly: return 'צפייה בלבד';
    default: return 'עובד/ת';
  }
});

const whatsapp = () => sendWhatsApp(props.member.phoneNumber);

// Mirrors the server default: an unset flag means the employee may see contact details.
// Settings load after mount, so re-sync whenever the resolved value arrives — otherwise a
// hard refresh latches the default `true` and the toggle lies about the stored state.
const contactAccess = ref(props.member.canViewCustomerContact !== false);
watch(
  () => props.member.canViewCustomerContact,
  value => { contactAccess.value = value !== false; }
);
const savingContactAccess = ref(false);

const updateContactAccess = async (value: boolean) => {
  const previous = contactAccess.value;
  contactAccess.value = value;
  savingContactAccess.value = true;
  try {
    await SettingsApi.updateEmployeeContactAccess(props.member.userId, value);
    await settingsStore.fetchBusinessSettings();
    toast.success(value ? 'העובד/ת יראה/תראה פרטי קשר של לקוחות' : 'פרטי הקשר של הלקוחות יוסתרו מהעובד/ת');
  } catch (e) {
    console.error(e);
    contactAccess.value = previous;
    toast.error('לא הצלחנו לעדכן את ההרשאה');
  } finally {
    savingContactAccess.value = false;
  }
};

// Names the branches this employee actually works at, so the toggle says what it
// unlocks. Falls back to a generic wording when they aren't assigned anywhere yet.
const branchScopeLabel = computed(() => {
  const names = branches.value.map(b => b.name).filter(Boolean);
  if (!names.length) return 'סניף שלו/ה';
  return names.join(' ו');
});

// Opt-in on the server (absent === no access), so an unset flag reads as off.
const branchCalendarAccess = ref(props.member.canViewBranchCalendar === true);
watch(
  () => props.member.canViewBranchCalendar,
  value => { branchCalendarAccess.value = value === true; }
);
const savingBranchCalendarAccess = ref(false);

const updateBranchCalendarAccess = async (value: boolean) => {
  const previous = branchCalendarAccess.value;
  branchCalendarAccess.value = value;
  savingBranchCalendarAccess.value = true;
  try {
    await SettingsApi.updateEmployeeBranchCalendarAccess(props.member.userId, value);
    await settingsStore.fetchBusinessSettings();
    toast.success(value ? 'העובד/ת יראה/תראה את יומן הסניף' : 'העובד/ת יראה/תראה רק את היומן האישי');
  } catch (e) {
    console.error(e);
    branchCalendarAccess.value = previous;
    toast.error('לא הצלחנו לעדכן את ההרשאה');
  } finally {
    savingBranchCalendarAccess.value = false;
  }
};

// Calendar defaults: which view the employee lands on, and whose appointments it starts
// scoped to. `scope` needs the branch-calendar grant to mean anything, so the server
// forces it back to "self" when that is off.
const VIEW_OPTIONS: Array<{ value: CalendarDefaultView; label: string }> = [
  { value: 'day', label: 'יומי' },
  { value: 'week', label: 'שבועי' },
  { value: 'list', label: 'רשימה' }
];
const SCOPE_OPTIONS: Array<{ value: CalendarDefaultScope; label: string }> = [
  { value: 'self', label: 'היומן שלו/ה' },
  { value: 'branch', label: 'יומן הסניף' }
];

const defaultView = ref<CalendarDefaultView>(props.member.calendarDefaults?.view ?? 'day');
const defaultScope = ref<CalendarDefaultScope>(props.member.calendarDefaults?.scope ?? 'self');
watch(
  () => props.member.calendarDefaults,
  value => {
    defaultView.value = value?.view ?? 'day';
    defaultScope.value = value?.scope ?? 'self';
  }
);
const savingCalendarDefaults = ref(false);

const updateCalendarDefaults = async (change: ICalendarDefaults) => {
  const previous = { view: defaultView.value, scope: defaultScope.value };
  if (change.view) defaultView.value = change.view;
  if (change.scope) defaultScope.value = change.scope;
  savingCalendarDefaults.value = true;
  try {
    await SettingsApi.updateEmployeeCalendarDefaults(props.member.userId, {
      view: defaultView.value,
      scope: defaultScope.value
    });
    await settingsStore.fetchBusinessSettings();
    toast.success('ברירת המחדל ליומן עודכנה');
  } catch (e) {
    console.error(e);
    defaultView.value = previous.view;
    defaultScope.value = previous.scope;
    toast.error('לא הצלחנו לעדכן את ברירת המחדל');
  } finally {
    savingCalendarDefaults.value = false;
  }
};

const confirmDeactivate = () => {
  confirm.require({
    message: `להשבית את ${props.member.fullName}? העובד/ת לא יופיע/תופיע ביומן ובהזמנות.`,
    header: 'השבתת עובד/ה',
    rejectLabel: 'ביטול',
    acceptLabel: 'השבתה',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await SettingsApi.deactivateEmployee(props.member.email);
        await Promise.all([adminStore.fetchBusinessData(), settingsStore.fetchBusinessSettings()]);
        toast.success('העובד/ת הושבת/ה');
        router.push({ name: AdminRouteNames.Team });
      } catch (e) {
        console.error(e);
        toast.error('לא הצלחנו להשבית את העובד/ת');
      }
    },
  });
};
</script>
